/* eslint-disable no-undef */
import { Environment } from 'src/Environment';

interface IResponse<T> {
  response: T;
  status: number;
  url: string;
}

function isValidUrl(url: string): boolean {
  // Regular expression to match a URL
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  return urlPattern.test(url);
}

export class Api {
  static getUrl(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl = (Environment.current?.apiHost || '') + (Environment.current?.apiBase || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  static getV2Url(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl = (Environment.current?.apiHost || '') + (Environment.current?.apiBaseV2 || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  static getApiUrl(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl = (Environment.current?.apiHost || '') + (Environment.current?.newApiBase || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  static getV2ApiUrl(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl = (Environment.current?.apiHost || '') + (Environment.current?.newApiBaseV2 || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  static getPrivateApiUrl(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl =
      (Environment.current?.apiHost || '') + (Environment.current?.privateApiBase || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  static getV2PrivateApiUrl(url: string): string {
    if (isValidUrl(url)) return url;

    let baseUrl =
      (Environment.current?.apiHost || '') + (Environment.current?.privateApiBaseV2 || '');
    if (!baseUrl.endsWith('/')) {
      baseUrl = baseUrl + '/';
    }
    return baseUrl + url;
  }

  //***** V1 *****
  static get<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.request<T>(url, options);
  }

  static post<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.request<T>(url, options);
  }

  static patch<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.request<T>(url, options);
  }

  static request<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = this.getUrl(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }

  //***** V2 *****
  static getV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.requestV2<T>(url, options);
  }

  static postV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.requestV2<T>(url, options);
  }

  static patchV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.requestV2<T>(url, options);
  }

  static requestV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = Api.getV2Url(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }

  //***** New API base V1 (NI) *****
  static getApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.requestApi<T>(url, options);
  }

  static postApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.requestApi<T>(url, options);
  }

  static patchApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.requestApi<T>(url, options);
  }

  static requestApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = Api.getApiUrl(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }

  //***** New API base V2 (NI) *****
  static getApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.requestApiV2<T>(url, options);
  }

  static postApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.requestApiV2<T>(url, options);
  }

  static patchApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.requestApiV2<T>(url, options);
  }

  static requestApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = Api.getV2ApiUrl(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }

  // ***** Private API Base *****
  static getPrivateApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.requestPrivateApi<T>(url, options);
  }

  static postPrivateApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.requestPrivateApi<T>(url, options);
  }

  static patchPrivateApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.requestPrivateApiV2<T>(url, options);
  }

  static requestPrivateApi<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = Api.getPrivateApiUrl(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }

  // ***** Private API Base V2 *****
  static getPrivateApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'GET';

    return Api.requestPrivateApiV2<T>(url, options);
  }

  static postPrivateApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'POST';

    return Api.requestPrivateApiV2<T>(url, options);
  }

  static patchPrivateApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    options.method = 'PATCH';

    return Api.requestPrivateApiV2<T>(url, options);
  }

  static requestPrivateApiV2<T>(url: string, options: RequestInit): Promise<IResponse<T>> {
    const _url = Api.getV2PrivateApiUrl(url);

    return fetch(_url, options).then(async (res) => ({
      response: (await res.json()) as T,
      status: res.status,
      url: res.url,
    }));
  }
}
